import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Timestamp, addDoc, collection, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { ConfirmModal } from '../../components/ConfirmModal';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function fmtDaysLeft(ts: any): string | null {
  const ms = tsToMs(ts);
  if (!ms) return null;
  const d = Math.ceil((ms - Date.now()) / (24 * 60 * 60 * 1000));
  if (d < 0) return 'Ended';
  if (d === 0) return 'Today';
  return `${d}d remaining`;
}

function fmtDate(ts: any): string {
  const ms = tsToMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtStatus(s: string): string {
  const map: Record<string, string> = {
    interested: 'Interested',
    applied: 'Applied',
    phone_screen: 'Phone Screen',
    interview: 'Interview',
    offer: 'Offer',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn',
    landed: 'Landed',
  };
  return map[s] ?? s;
}

interface Profile {
  name: string;
  email: string;
  enrollmentStatus: 'pending' | 'approved' | null;
  engagementStatus: 'active' | 'placed' | 'paused' | null;
  lastJobActivityAt: any;
  lastActiveAt: any;
  enrolledAt: any;
  programEndDate: any;
  resumeLink: string | null;
  resumeLinkUpdatedAt: any;
  resumeLinkUpdatedBy: string | null;
  linkedinUrl: string | null;
  nextSessionDate: any;
  resumeUpdatedAt: string | null;
  // Search Direction
  field: string | null;
  companyGroups: string[] | null;
  jobTimeline: string | null;
  experienceLevel: string | null;
  specialization: string | null;
  // Candidate Background
  skills: string[] | null;
  phase1Score: number | null;
  phase1Note: string | null;
  phase2Score: number | null;
  phase2Note: string | null;
  phase3Score: number | null;
  phase3Note: string | null;
  university: string | null;
  degree: string | null;
  graduationYear: string | null;
  internships: string | null;
  hackathons: string | null;
  projects: Array<{ name: string; description: string }> | null;
  aboutYou: string | null;
}

interface StatusHistoryEntry {
  status: string;
  changedAt: any;
}

interface FitComponents {
  role_alignment: number;
  experience_match: number;
  track_record: number;
  skills_match: number;
}

interface SkillGap {
  skill: string;
  importance: 'critical' | 'important' | 'nice_to_have';
  reason?: string | null;
  bridge?: string | null;
}

interface JobDetail {
  title: string;
  company: string;
  date: string;
  location: string | null;
  salary: string | null;
  input: string | null;
  fit: number | null;
  components: FitComponents | null;
  skillsPresent: string[] | null;
  skillsMissing: SkillGap[] | null;
  statusHistory: StatusHistoryEntry[];
}

interface Job {
  id: string;
  company: string;
  role: string;
  status: string;
  fit: number | null;
  createdAt: any;
  updatedAt: any;
  statusHistory: StatusHistoryEntry[];
}

interface LogEntry {
  id: string;
  type: 'ai' | 'coach';
  timestampMs: number;
  insightText?: string;
  stats?: string;
  lens?: string;
  text?: string;
  visibility?: 'private' | 'shared';
  category?: NoteCategory;
}

type NoteCategory = 'mock_interview' | 'resume_review' | 'offer_negotiation' | 'job_search_strategy' | 'linkedin_branding' | 'general_checkin' | 'other';

const NOTE_CATEGORIES: NoteCategory[] = ['mock_interview', 'resume_review', 'offer_negotiation', 'job_search_strategy', 'linkedin_branding', 'general_checkin', 'other'];

const CATEGORY_LABELS: Record<NoteCategory, string> = {
  mock_interview: 'Mock Interview',
  resume_review: 'Resume Review',
  offer_negotiation: 'Offer Negotiation',
  job_search_strategy: 'Job Search Strategy',
  linkedin_branding: 'LinkedIn/Branding',
  general_checkin: 'General Check-in',
  other: 'Other',
};

const STATUS_ORDER = ['interested', 'applied', 'phone_screen', 'interview', 'offer', 'landed', 'rejected', 'withdrawn'];

const FIELD_LABELS: Record<string, string> = {
  cs: 'Computer Science / Engineering',
  finance: 'Finance',
  data_science: 'Data Science',
  design: 'Product Design',
  business: 'Business / Strategy',
  health: 'Healthcare / Life Sciences',
  premed: 'Pre-Medicine',
  natural_sciences: 'Natural Sciences',
  law: 'Law',
  liberal_arts: 'Liberal Arts',
};

function tsToDateInput(ts: any): string {
  if (!ts) return '';
  const ms = tsToMs(ts);
  if (!ms) return '';
  const d = new Date(ms);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${dy}`;
}

function historyMs(changedAt: any): number {
  if (!changedAt) return 0;
  if (typeof changedAt.toMillis === 'function') return changedAt.toMillis();
  if (typeof changedAt.seconds === 'number') return changedAt.seconds * 1000;
  if (changedAt instanceof Date) return changedAt.getTime();
  if (typeof changedAt === 'string') { const t = new Date(changedAt).getTime(); return isNaN(t) ? 0 : t; }
  return 0;
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function medianDays(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? Math.round((sorted[mid - 1] + sorted[mid]) / 2)
    : sorted[mid];
}

function normalizeSkillGaps(raw: any[]): SkillGap[] {
  return raw.map(item =>
    typeof item === 'string'
      ? { skill: item, importance: 'important' as const, reason: null, bridge: null }
      : (item as SkillGap)
  );
}

const SKILL_TIERS: Array<{ key: SkillGap['importance']; label: string }> = [
  { key: 'critical',     label: 'Critical'     },
  { key: 'important',    label: 'Important'    },
  { key: 'nice_to_have', label: 'Nice to Have' },
];

const SCORE_COMPONENTS: Array<{ key: keyof FitComponents; label: string; sub: string }> = [
  { key: 'role_alignment',   label: 'Role Alignment',   sub: 'How well the role type and level match'           },
  { key: 'experience_match', label: 'Experience Match', sub: 'How closely your background maps to requirements' },
  { key: 'track_record',     label: 'Track Record',     sub: 'Evidence of real impact and delivered work'       },
  { key: 'skills_match',     label: 'Skills Match',     sub: 'Technical and functional skill overlap'           },
];

export function CandidateDetail() {
  const { uid } = useParams<{ uid: string }>();
  const { coach } = useAuth();
  const navigate = useNavigate();
  const coachId = coach!.coachId;

  const [profile, setProfile] = useState<Profile | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [candidateFeed, setCandidateFeed] = useState<{ id: string; timestampMs: number; text: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [pendingAccess, setPendingAccess] = useState(false);

  const [resumeLinkDraft, setResumeLinkDraft] = useState('');
  const [resumeLinkEditing, setResumeLinkEditing] = useState(false);
  const [savingResumeLink, setSavingResumeLink] = useState(false);
  const [resumeLinkError, setResumeLinkError] = useState('');

  const [noteText, setNoteText] = useState('');
  const [noteVisibility, setNoteVisibility] = useState<'private' | 'shared'>('private');
  const [noteCategory, setNoteCategory] = useState<NoteCategory>('general_checkin');
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState('');

  const [savingEngagement, setSavingEngagement] = useState(false);

  const [nextSessionDraft, setNextSessionDraft] = useState('');
  const [savingNextSession, setSavingNextSession] = useState(false);
  const [nextSessionError, setNextSessionError] = useState('');

  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  const [searchQualitySnap, setSearchQualitySnap] = useState<any>(null);

  const [sinceLastVisit, setSinceLastVisit] = useState<{
    daysAgo: number;
    newJobs: number;
    statusChanges: number;
    newNotes: number;
  } | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const loadLog = async (): Promise<LogEntry[]> => {
    try {
      const [aiSnap, notesSnap] = await Promise.all([
        getDocs(collection(db, 'coachingLog', uid!, 'entries')),
        getDocs(collection(db, 'coachNotes', uid!, 'entries')),
      ]);
      const entries: LogEntry[] = [];
      for (const d of aiSnap.docs) {
        const e = d.data();
        entries.push({
          id: `ai-${d.id}`,
          type: 'ai',
          timestampMs: tsToMs(e.timestamp),
          insightText: e.insightText ?? '',
          stats: e.stats ?? '',
          lens: e.lens ?? '',
        });
      }
      for (const d of notesSnap.docs) {
        const e = d.data();
        entries.push({
          id: `coach-${d.id}`,
          type: 'coach',
          timestampMs: tsToMs(e.createdAt),
          text: e.text ?? '',
          visibility: e.visibility ?? 'private',
          category: (e.category as NoteCategory) ?? 'general_checkin',
        });
      }
      entries.sort((a, b) => b.timestampMs - a.timestampMs);
      setLogEntries(entries);
      return entries;
    } catch {
      return [];
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    setPendingAccess(false);
    setSinceLastVisit(null);
    setBannerDismissed(false);
    let profileLoaded = false;
    try {
      const profileSnap = await getDoc(doc(db, 'profiles', uid!));
      if (!profileSnap.exists()) {
        setError('Candidate not found.');
        return;
      }
      const p = profileSnap.data();
      const prevLastViewed = p.lastViewedByCoachAt ?? null;
      if (p.coachId !== coachId) {
        navigate('/coach/caseload', { replace: true });
        return;
      }
      setProfile({
        name: p.manual_name ?? p.name ?? p.displayName ?? '',
        email: p.authEmail ?? '',
        enrollmentStatus: p.enrollmentStatus ?? null,
        engagementStatus: p.engagementStatus ?? null,
        lastJobActivityAt: p.lastJobActivityAt,
        lastActiveAt: p.lastActiveAt,
        enrolledAt: p.enrolledAt,
        programEndDate: p.programEndDate ?? null,
        resumeLink: p.resumeLink ?? null,
        resumeLinkUpdatedAt: p.resumeLinkUpdatedAt ?? null,
        resumeLinkUpdatedBy: p.resumeLinkUpdatedBy ?? null,
        linkedinUrl: p.linkedinUrl ?? null,
        nextSessionDate: p.nextSessionDate ?? null,
        resumeUpdatedAt: typeof p.resumeUpdatedAt === 'string' ? p.resumeUpdatedAt : null,
        field: p.field ?? null,
        companyGroups: Array.isArray(p.companyGroups) && p.companyGroups.length > 0 ? p.companyGroups : null,
        jobTimeline: p.jobTimeline ?? null,
        experienceLevel: p.experienceLevel ?? null,
        specialization: p.specialization ?? null,
        skills: Array.isArray(p.skills) && p.skills.length > 0 ? p.skills : null,
        phase1Score: typeof p.phase_1_score === 'number' && p.phase_1_score > 0 ? p.phase_1_score : null,
        phase1Note: p.phase_1_note ?? null,
        phase2Score: typeof p.phase_2_score === 'number' && p.phase_2_score > 0 ? p.phase_2_score : null,
        phase2Note: p.phase_2_note ?? null,
        phase3Score: typeof p.phase_3_score === 'number' && p.phase_3_score > 0 ? p.phase_3_score : null,
        phase3Note: p.phase_3_note ?? null,
        university: p.university ?? null,
        degree: p.degree ?? null,
        graduationYear: p.graduation_year != null ? String(p.graduation_year) : null,
        internships: p.internships ?? null,
        hackathons: p.hackathons ?? null,
        projects: Array.isArray(p.projects) && p.projects.length > 0 ? p.projects : null,
        aboutYou: p.about_you ?? null,
      });
      setNextSessionDraft(tsToDateInput(p.nextSessionDate));
      profileLoaded = true;

      // Candidate hasn't accepted yet — skip all further reads (they'd be denied).
      if (p.enrollmentStatus === 'pending') {
        setPendingAccess(true);
        return;
      }

      const jobsSnap = await getDocs(query(collection(db, 'jobs'), where('userId', '==', uid)));
      const jobList: Job[] = jobsSnap.docs.map(d => {
        const j = d.data();
        return {
          id: d.id,
          company: j.company ?? '',
          role: j.role ?? j.title ?? '',
          status: j.status ?? '',
          fit: j.fit ?? null,
          createdAt: j.createdAt,
          updatedAt: j.updatedAt,
          statusHistory: j.statusHistory ?? [],
        };
      });
      jobList.sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt));
      setJobs(jobList);

      const entries = await loadLog();

      const candNotesSnap = await getDocs(query(
        collection(db, 'candidateNotes', uid!, 'entries'),
        where('visibility', '==', 'shared'),
      ));
      setCandidateFeed(
        candNotesSnap.docs
          .map(d => ({ id: d.id, timestampMs: tsToMs(d.data().createdAt), text: d.data().text ?? '' }))
          .sort((a, b) => b.timestampMs - a.timestampMs),
      );

      try {
        const sqSnap = await getDocs(query(
          collection(db, 'searchQuality', uid!, 'snapshots'),
          orderBy('weekId', 'desc'),
          limit(1),
        ));
        setSearchQualitySnap(sqSnap.empty ? null : sqSnap.docs[0].data());
      } catch {}

      if (prevLastViewed) {
        const prevMs = tsToMs(prevLastViewed);
        const newJobs = jobList.filter(j => tsToMs(j.createdAt) > prevMs).length;
        const statusChanges = jobList.filter(j =>
          j.statusHistory.some(sh => historyMs(sh.changedAt) > prevMs)
        ).length;
        const newNotes = entries.filter(entry => entry.timestampMs > prevMs).length;
        if (newJobs > 0 || statusChanges > 0 || newNotes > 0) {
          const daysAgo = Math.floor((Date.now() - prevMs) / (24 * 60 * 60 * 1000));
          setSinceLastVisit({ daysAgo, newJobs, statusChanges, newNotes });
        }
      }

      updateDoc(doc(db, 'profiles', uid!), { lastViewedByCoachAt: serverTimestamp() }).catch(() => {});
    } catch (e: any) {
      // Only treat permission-denied as pending if the profile itself couldn't be read.
      // A permission-denied after profileLoaded means a secondary read failed (e.g. jobs
      // rule gap) — don't hide the profile view over a partial data failure.
      if (!profileLoaded) {
        if ((e?.code ?? '') === 'permission-denied') {
          setPendingAccess(true);
        } else {
          setError(e.message || 'Failed to load candidate.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (uid) load();
  }, [uid]);

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    setNoteError('');
    try {
      await addDoc(collection(db, 'coachNotes', uid!, 'entries'), {
        text: noteText.trim(),
        visibility: noteVisibility,
        category: noteCategory,
        createdAt: serverTimestamp(),
      });
      setNoteText('');
      setNoteVisibility('private');
      setNoteCategory('general_checkin');
      await loadLog();
    } catch (e: any) {
      setNoteError(e.message || 'Failed to save note.');
    } finally {
      setSavingNote(false);
    }
  };

  const saveResumeLink = async () => {
    const url = resumeLinkDraft.trim();
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      setResumeLinkError('URL must start with http:// or https://');
      return;
    }
    setSavingResumeLink(true);
    setResumeLinkError('');
    try {
      await updateDoc(doc(db, 'profiles', uid!), {
        resumeLink: url || null,
        resumeLinkUpdatedAt: serverTimestamp(),
        resumeLinkUpdatedBy: coachId,
      });
      setProfile(p => p ? { ...p, resumeLink: url || null, resumeLinkUpdatedBy: coachId, resumeLinkUpdatedAt: new Date() } : p);
      setResumeLinkEditing(false);
      setResumeLinkDraft('');
    } catch (e: any) {
      setResumeLinkError(e.message || 'Failed to save.');
    } finally {
      setSavingResumeLink(false);
    }
  };

  const saveNextSession = async (value: string) => {
    setSavingNextSession(true);
    setNextSessionError('');
    try {
      const ts = value ? Timestamp.fromDate(new Date(value + 'T12:00:00')) : null;
      await updateDoc(doc(db, 'profiles', uid!), { nextSessionDate: ts });
      setProfile(p => p ? { ...p, nextSessionDate: ts } : p);
      setNextSessionDraft(value);
    } catch (e: any) {
      setNextSessionError(e.message || 'Failed to save.');
    } finally {
      setSavingNextSession(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    setRemoveError('');
    try {
      await httpsCallable(getFunctions(), 'removeCandidate')({ candidateUid: uid });
      navigate('/coach/caseload');
    } catch (e: any) {
      setRemoveError(e.message || 'Failed to remove candidate.');
      setRemoving(false);
    }
  };

  const thirtyDayMs = 30 * 24 * 60 * 60 * 1000;
  const recentCount = jobs.filter(j => tsToMs(j.createdAt) > Date.now() - thirtyDayMs).length;

  const statusCounts: Record<string, number> = {};
  for (const j of jobs) {
    const s = j.status || 'unknown';
    statusCounts[s] = (statusCounts[s] ?? 0) + 1;
  }
  const sortedStatuses = Object.keys(statusCounts).sort(
    (a, b) => (STATUS_ORDER.indexOf(a) === -1 ? 99 : STATUS_ORDER.indexOf(a))
            - (STATUS_ORDER.indexOf(b) === -1 ? 99 : STATUS_ORDER.indexOf(b))
  );

  const MIN_METRIC_N = 5;
  const RESPONSE_SET = new Set(['phone_screen', 'interview', 'offer', 'accepted', 'landed', 'rejected']);
  const INTERVIEW_SET = new Set(['phone_screen', 'interview', 'offer', 'accepted', 'landed']);
  const EVER_APPLIED_SET = new Set(['applied', 'no_response', 'phone_screen', 'interview', 'offer', 'accepted', 'landed', 'rejected', 'withdrawn']);

  const appliedPool = jobs.filter(j =>
    j.statusHistory.length > 0
      ? j.statusHistory.some(e => e.status === 'applied')
      : EVER_APPLIED_SET.has(j.status)
  );
  const appliedN = appliedPool.length;

  const respondedN = appliedPool.filter(j =>
    j.statusHistory.length > 0
      ? j.statusHistory.some(e => RESPONSE_SET.has(e.status))
      : RESPONSE_SET.has(j.status)
  ).length;

  const interviewedN = appliedPool.filter(j =>
    j.statusHistory.length > 0
      ? j.statusHistory.some(e => INTERVIEW_SET.has(e.status))
      : INTERVIEW_SET.has(j.status)
  ).length;

  const ghostedN = appliedPool.filter(j => {
    if (j.statusHistory.length > 0) {
      const last = [...j.statusHistory].sort((a, b) => historyMs(b.changedAt) - historyMs(a.changedAt))[0];
      return (last.status === 'applied' || last.status === 'no_response')
        && (Date.now() - historyMs(last.changedAt)) > thirtyDayMs;
    }
    return (j.status === 'applied' || j.status === 'no_response')
      && (Date.now() - tsToMs(j.updatedAt)) > thirtyDayMs;
  }).length;

  const daysToResponseList: number[] = [];
  const daysToRejectionList: number[] = [];
  for (const j of appliedPool) {
    if (j.statusHistory.length < 2) continue;
    const hist = [...j.statusHistory].sort((a, b) => historyMs(a.changedAt) - historyMs(b.changedAt));
    const appliedEntry = hist.find(e => e.status === 'applied');
    if (!appliedEntry) continue;
    const appliedMs = historyMs(appliedEntry.changedAt);
    if (!appliedMs) continue;
    const firstResp = hist.find(e => RESPONSE_SET.has(e.status) && historyMs(e.changedAt) > appliedMs);
    if (firstResp) {
      const d = Math.round((historyMs(firstResp.changedAt) - appliedMs) / 86400000);
      if (d >= 0) daysToResponseList.push(d);
    }
    const rejEntry = hist.find(e => e.status === 'rejected' && historyMs(e.changedAt) > appliedMs);
    if (rejEntry) {
      const d = Math.round((historyMs(rejEntry.changedAt) - appliedMs) / 86400000);
      if (d >= 0) daysToRejectionList.push(d);
    }
  }

  const marketMetrics = appliedN > 0 ? {
    n: appliedN,
    responseRate: appliedN >= MIN_METRIC_N ? Math.round((respondedN / appliedN) * 100) : null,
    responseN: respondedN,
    interviewRate: appliedN >= MIN_METRIC_N ? Math.round((interviewedN / appliedN) * 100) : null,
    interviewN: interviewedN,
    ghostingRate: appliedN >= MIN_METRIC_N ? Math.round((ghostedN / appliedN) * 100) : null,
    ghostedN,
    medianToResponse: daysToResponseList.length >= MIN_METRIC_N ? medianDays(daysToResponseList) : null,
    responseTimingN: daysToResponseList.length,
    medianToRejection: daysToRejectionList.length >= MIN_METRIC_N ? medianDays(daysToRejectionList) : null,
    rejectionTimingN: daysToRejectionList.length,
  } : null;

  const stripItems: { key: string; label: string; value: string }[] = [];
  if (profile) {
    if (profile.resumeUpdatedAt) {
      const d = new Date(profile.resumeUpdatedAt);
      if (!isNaN(d.getTime())) {
        stripItems.push({ key: 'resume', label: 'Resume updated', value: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) });
      }
    }
    const APPLIED_STRIP = new Set(['applied', 'no_response', 'phone_screen', 'interview', 'offer', 'accepted', 'landed', 'rejected', 'withdrawn']);
    const appliedThisWeek = jobs.filter(j =>
      tsToMs(j.createdAt) > Date.now() - 7 * 24 * 60 * 60 * 1000 && APPLIED_STRIP.has(j.status)
    ).length;
    if (appliedThisWeek > 0) {
      stripItems.push({ key: 'applied', label: 'Applied', value: `${appliedThisWeek} job${appliedThisWeek !== 1 ? 's' : ''} this week` });
    }
    if (profile.nextSessionDate && tsToMs(profile.nextSessionDate)) {
      stripItems.push({ key: 'session', label: 'Next session', value: fmtDate(profile.nextSessionDate) });
    }
  }

  return (
    <Layout>
      <div style={{ maxWidth: 800 }}>
        <div style={{ marginBottom: 20 }}>
          <Link
            to="/coach/caseload"
            style={{ fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}
          >
            ← Back to Caseload
          </Link>
        </div>

        {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

        {loading ? (
          <div className="loading">Loading…</div>
        ) : pendingAccess ? (
          <>
            {profile && (
              <div className="page-header" style={{ marginBottom: 24 }}>
                <div>
                  <h1 className="page-title">{profile.name || '—'}</h1>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, fontFamily: 'monospace' }}>
                    {profile.email}
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
                    <StatusBadge status="pending" />
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      Enrolled {fmtDate(profile.enrolledAt)}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div style={{
              padding: '20px 24px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 14,
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
            }}>
              This candidate hasn't approved the coaching connection yet. You'll be able to view their details once they accept the invitation in the app.
            </div>
          </>
        ) : profile ? (
          <>
            {/* Header */}
            <div className="page-header" style={{ marginBottom: 24 }}>
              <div>
                <h1 className="page-title">{profile.name || '—'}</h1>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, fontFamily: 'monospace' }}>
                  {profile.email}
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
                  <StatusBadge status={profile.enrollmentStatus} />
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    Enrolled {fmtDate(profile.enrolledAt)}
                  </span>
                  {profile.programEndDate && (
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      · {fmtDaysLeft(profile.programEndDate)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Changes since last visit */}
            {sinceLastVisit && !bannerDismissed && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
                <span>
                  Since your last visit ({sinceLastVisit.daysAgo === 0 ? 'today' : sinceLastVisit.daysAgo === 1 ? '1 day ago' : `${sinceLastVisit.daysAgo} days ago`}):{' '}
                  {[
                    sinceLastVisit.newJobs > 0 ? `${sinceLastVisit.newJobs} new application${sinceLastVisit.newJobs !== 1 ? 's' : ''}` : null,
                    sinceLastVisit.statusChanges > 0 ? `${sinceLastVisit.statusChanges} status change${sinceLastVisit.statusChanges !== 1 ? 's' : ''}` : null,
                    sinceLastVisit.newNotes > 0 ? `${sinceLastVisit.newNotes} new note${sinceLastVisit.newNotes !== 1 ? 's' : ''}` : null,
                  ].filter(Boolean).join(', ')}
                </span>
                <button
                  type="button"
                  onClick={() => setBannerDismissed(true)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-muted)', padding: '0 4px', lineHeight: 1, marginLeft: 12, flexShrink: 0 }}
                  aria-label="Dismiss"
                >×</button>
              </div>
            )}

            {/* Status strip */}
            {stripItems.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 28px', marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                {stripItems.map(item => (
                  <div key={item.key} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    <span style={{ color: 'var(--text-muted)' }}>{item.label}</span>
                    {' · '}
                    <span>{item.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Engagement Status + Next Session */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                Engagement
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['active', 'placed', 'paused'] as const).map(val => {
                  const current = profile.engagementStatus ?? 'active';
                  const isSelected = current === val;
                  return (
                    <button
                      key={val}
                      type="button"
                      disabled={savingEngagement || isSelected}
                      onClick={async () => {
                        setSavingEngagement(true);
                        try {
                          await updateDoc(doc(db, 'profiles', uid!), { engagementStatus: val });
                          setProfile(p => p ? { ...p, engagementStatus: val } : p);
                        } catch {
                          // silent — not worth blocking UI for a simple toggle
                        } finally {
                          setSavingEngagement(false);
                        }
                      }}
                      style={{
                        padding: '4px 14px',
                        fontSize: 12,
                        borderRadius: 4,
                        border: '1px solid var(--border)',
                        cursor: isSelected ? 'default' : 'pointer',
                        background: isSelected ? 'var(--accent)' : 'var(--bg-secondary)',
                        color: isSelected ? '#fff' : 'var(--text-secondary)',
                        fontWeight: isSelected ? 600 : 400,
                        opacity: savingEngagement ? 0.6 : 1,
                      }}
                    >
                      {val.charAt(0).toUpperCase() + val.slice(1)}
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  Next Session
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="date"
                    className="form-input"
                    value={nextSessionDraft}
                    onChange={e => setNextSessionDraft(e.target.value)}
                    onBlur={e => saveNextSession(e.target.value)}
                    disabled={savingNextSession}
                    style={{ maxWidth: 200, margin: 0 }}
                  />
                  {nextSessionDraft && (
                    <button
                      type="button"
                      onClick={() => saveNextSession('')}
                      disabled={savingNextSession}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)', padding: '0 4px' }}
                    >
                      Clear
                    </button>
                  )}
                  {savingNextSession && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Saving…</span>}
                  {nextSessionError && <span style={{ fontSize: 12, color: 'var(--error)' }}>{nextSessionError}</span>}
                </div>
              </div>
            </div>

            {/* Candidate Background */}
            {(profile.skills || profile.phase1Score != null || profile.university || profile.degree || profile.internships || profile.hackathons || profile.aboutYou || profile.projects) && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
                  Candidate Background
                </div>
                <div style={{ display: 'grid', gap: 14 }}>
                  {(profile.phase1Score != null || profile.phase2Score != null || profile.phase3Score != null) && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 7 }}>Resume Scores</div>
                      <div style={{ display: 'grid', gap: 5 }}>
                        {profile.phase1Score != null && (
                          <FactRow label="Skills Match" value={`${profile.phase1Score}/10${profile.phase1Note && profile.phase1Note.length <= 120 ? `  —  ${profile.phase1Note}` : ''}`} />
                        )}
                        {profile.phase2Score != null && (
                          <FactRow label="Experience Depth" value={`${profile.phase2Score}/10${profile.phase2Note && profile.phase2Note.length <= 120 ? `  —  ${profile.phase2Note}` : ''}`} />
                        )}
                        {profile.phase3Score != null && (
                          <FactRow label="Presentation" value={`${profile.phase3Score}/10${profile.phase3Note && profile.phase3Note.length <= 120 ? `  —  ${profile.phase3Note}` : ''}`} />
                        )}
                      </div>
                    </div>
                  )}
                  {profile.skills && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 7 }}>Skills</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {profile.skills.map((s, i) => (
                          <span key={i} style={{ fontSize: 12, padding: '2px 8px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)' }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(profile.university || profile.degree) && (
                    <FactRow
                      label="Education"
                      value={[profile.university, profile.degree, profile.graduationYear ? `Class of ${profile.graduationYear}` : null].filter(Boolean).join(' · ')}
                    />
                  )}
                  {profile.internships && <FactRow label="Internships" value={profile.internships} />}
                  {profile.hackathons && <FactRow label="Hackathons" value={profile.hackathons} />}
                  {profile.aboutYou && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 5 }}>About</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>{profile.aboutYou}</div>
                    </div>
                  )}
                  {profile.projects && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 7 }}>Projects</div>
                      <div style={{ display: 'grid', gap: 5 }}>
                        {profile.projects.map((proj, i) => (
                          <div key={i} style={{ fontSize: 13 }}>
                            <span style={{ fontWeight: 500 }}>{proj.name}</span>
                            {proj.description && <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>— {proj.description}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Search Direction */}
            {(profile.field || profile.companyGroups || profile.jobTimeline || profile.experienceLevel || profile.specialization) && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
                  Search Direction
                </div>
                <div style={{ display: 'grid', gap: 10 }}>
                  {profile.field && <FactRow label="Field" value={FIELD_LABELS[profile.field] ?? profile.field} />}
                  {profile.specialization && <FactRow label="Specialization" value={profile.specialization} />}
                  {profile.experienceLevel && <FactRow label="Level" value={profile.experienceLevel} />}
                  {profile.jobTimeline && <FactRow label="Timeline" value={profile.jobTimeline} />}
                  {profile.companyGroups && (
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 5 }}>Target Companies</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {profile.companyGroups.map((g, i) => (
                          <span key={i} style={{ fontSize: 12, padding: '2px 8px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)' }}>{g}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Resume Link */}
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              Resume Link
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              A shared link to the candidate's resume (Google Docs, Word Online, etc.) — either of you can add or update it.
            </div>
            <div className="card" style={{ marginBottom: 24, padding: '14px 20px' }}>
              {resumeLinkEditing || !profile.resumeLink ? (
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      className="form-input"
                      type="url"
                      placeholder="https://…"
                      value={resumeLinkDraft}
                      onChange={e => { setResumeLinkDraft(e.target.value); setResumeLinkError(''); }}
                      style={{ flex: 1, fontFamily: 'inherit' }}
                    />
                    <button
                      className="btn-primary"
                      onClick={saveResumeLink}
                      disabled={savingResumeLink}
                      style={{ padding: '8px 16px', fontSize: 13, whiteSpace: 'nowrap' }}
                    >
                      {savingResumeLink ? 'Saving…' : 'Save'}
                    </button>
                    {resumeLinkEditing && (
                      <button
                        type="button"
                        onClick={() => { setResumeLinkEditing(false); setResumeLinkDraft(''); setResumeLinkError(''); }}
                        style={{ padding: '8px 12px', fontSize: 13, background: 'none', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-secondary)', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                  {resumeLinkError && (
                    <div className="error-message" style={{ marginTop: 8 }}>{resumeLinkError}</div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <a
                      href={profile.resumeLink}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 13, color: 'var(--accent-text)', wordBreak: 'break-all' }}
                    >
                      {profile.resumeLink}
                    </a>
                    {profile.resumeLinkUpdatedAt && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                        {fmtDate(profile.resumeLinkUpdatedAt)}
                        {' · '}
                        {profile.resumeLinkUpdatedBy === coachId ? 'last updated by you' : 'last updated by candidate'}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setResumeLinkDraft(profile.resumeLink ?? ''); setResumeLinkEditing(true); }}
                    style={{ marginLeft: 16, padding: '6px 12px', fontSize: 12, background: 'none', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>

            {/* LinkedIn URL (read-only) */}
            {profile.linkedinUrl && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                  LinkedIn
                </div>
                <div className="card" style={{ padding: '14px 20px' }}>
                  <a
                    href={profile.linkedinUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 13, color: 'var(--accent-text)', wordBreak: 'break-all' }}
                  >
                    {profile.linkedinUrl}
                  </a>
                </div>
              </div>
            )}

            {/* Job search activity */}
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              Job Search Activity
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
              <div className="stat-card">
                <div className="stat-label">Total Jobs</div>
                <div className="stat-value">{jobs.length}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Added (last 30d)</div>
                <div className="stat-value">{recentCount}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Last Activity</div>
                <div className="stat-value" style={{ fontSize: 16 }}>
                  {fmtDate(profile.lastJobActivityAt ?? profile.lastActiveAt)}
                </div>
              </div>
            </div>

            {/* Status breakdown */}
            {sortedStatuses.length > 0 && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
                  By Status
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                  {sortedStatuses.map(s => (
                    <div key={s} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ fontSize: 22, fontWeight: 700 }}>{statusCounts[s]}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{fmtStatus(s)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Market Response */}
            {marketMetrics && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
                  Market Response
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 32px' }}>
                  <MarketStat
                    value={marketMetrics.responseRate !== null ? `${marketMetrics.responseRate}%` : '—'}
                    label="Response Rate"
                    sub={`${marketMetrics.responseN} of ${marketMetrics.n}`}
                  />
                  <MarketStat
                    value={marketMetrics.interviewRate !== null ? `${marketMetrics.interviewRate}%` : '—'}
                    label="Interview Rate"
                    sub={`${marketMetrics.interviewN} of ${marketMetrics.n}`}
                  />
                  <MarketStat
                    value={marketMetrics.ghostingRate !== null ? `${marketMetrics.ghostingRate}%` : '—'}
                    label="Ghosted (30d)"
                    sub={`${marketMetrics.ghostedN} of ${marketMetrics.n}`}
                  />
                  <MarketStat
                    value={marketMetrics.medianToResponse !== null ? `${marketMetrics.medianToResponse}d` : '—'}
                    label="Median to Response"
                    sub={`n=${marketMetrics.responseTimingN}`}
                  />
                  <MarketStat
                    value={marketMetrics.medianToRejection !== null ? `${marketMetrics.medianToRejection}d` : '—'}
                    label="Median to Rejection"
                    sub={`n=${marketMetrics.rejectionTimingN}`}
                  />
                </div>
              </div>
            )}

            {/* Search Quality */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Search Quality
                </div>
                {searchQualitySnap && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    week of {new Date(searchQualitySnap.weekId + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                )}
              </div>
              {!searchQualitySnap ? (
                <div style={{ padding: '8px 0 4px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  No search quality data yet — available once the candidate has some application activity.
                </div>
              ) : (
                <SearchQualityBody snap={searchQualitySnap} />
              )}
            </div>

            {/* Recent jobs table */}
            <div className="card" style={{ padding: 0, marginBottom: 32 }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Recent Jobs
              </div>
              {jobs.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  No jobs added yet.
                </div>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th style={{ textAlign: 'right' }}>
                          Fit Score{' '}
                          <span style={{
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            padding: '2px 5px',
                            borderRadius: 3,
                            background: 'var(--bg-secondary)',
                            color: 'var(--text-muted)',
                            border: '1px solid var(--border)',
                            verticalAlign: 'middle',
                          }}>AI</span>
                        </th>
                        <th>Added</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.slice(0, 10).map(j => (
                        <tr key={j.id} onClick={() => setSelectedJobId(j.id)} style={{ cursor: 'pointer' }}>
                          <td style={{ fontWeight: 500 }}>{j.company || '—'}</td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{j.role || '—'}</td>
                          <td style={{ fontSize: 13 }}>{fmtStatus(j.status)}</td>
                          <td style={{ textAlign: 'right', fontSize: 13 }}>
                            {j.fit != null ? j.fit : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                          </td>
                          <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{fmtDate(j.createdAt)}</td>
                          <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{fmtDate(j.updatedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {jobs.length > 10 && (
                <div style={{ padding: '10px 20px', fontSize: 12, color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>
                  Showing 10 of {jobs.length} total jobs.
                </div>
              )}
            </div>

            {/* Coaching log */}
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              Coaching Log
            </div>

            {/* Add note */}
            <div className="card" style={{ marginBottom: 16 }}>
              <textarea
                className="form-input"
                rows={3}
                placeholder="Add a coaching note…"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', marginBottom: 10 }}
              />
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 }}>
                  Category
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {NOTE_CATEGORIES.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setNoteCategory(cat)}
                      style={{
                        padding: '3px 10px',
                        fontSize: 12,
                        borderRadius: 4,
                        border: '1px solid var(--border)',
                        cursor: 'pointer',
                        background: noteCategory === cat ? 'var(--accent)' : 'var(--bg-secondary)',
                        color: noteCategory === cat ? '#fff' : 'var(--text-secondary)',
                        fontWeight: noteCategory === cat ? 600 : 400,
                      }}
                    >
                      {CATEGORY_LABELS[cat]}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {(['private', 'shared'] as const).map(v => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setNoteVisibility(v)}
                    style={{
                      padding: '4px 12px',
                      fontSize: 12,
                      borderRadius: 4,
                      border: '1px solid var(--border)',
                      cursor: 'pointer',
                      background: noteVisibility === v ? 'var(--accent)' : 'var(--bg-secondary)',
                      color: noteVisibility === v ? '#fff' : 'var(--text-secondary)',
                      fontWeight: noteVisibility === v ? 600 : 400,
                    }}
                  >
                    {v === 'private' ? '🔒 Private' : '👁 Share with candidate'}
                  </button>
                ))}
              </div>
              {noteError && (
                <div className="error-message" style={{ marginBottom: 10 }}>{noteError}</div>
              )}
              <button
                className="btn-primary"
                onClick={saveNote}
                disabled={savingNote || !noteText.trim()}
                style={{ padding: '8px 18px', fontSize: 13 }}
              >
                {savingNote ? 'Saving…' : 'Save Note'}
              </button>
            </div>

            {/* Merged log entries */}
            {logEntries.length === 0 ? (
              <div className="card" style={{ marginBottom: 32, padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                No log entries yet.
              </div>
            ) : (
              <div style={{ marginBottom: 32 }}>
                {logEntries.map(entry => (
                  <div
                    key={entry.id}
                    style={{
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: 16,
                      marginBottom: 10,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <EntryLabel type={entry.type} visibility={entry.visibility} />
                        {entry.type === 'coach' && entry.category && (
                          <CategoryTag category={entry.category} />
                        )}
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {entry.timestampMs
                          ? new Date(entry.timestampMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </span>
                    </div>
                    {entry.type === 'ai' ? (
                      <>
                        {entry.insightText && (
                          <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6, marginBottom: entry.stats ? 6 : 0 }}>
                            {entry.insightText}
                          </div>
                        )}
                        {entry.stats && (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{entry.stats}</div>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {entry.text}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Notes from candidate */}
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              Notes from candidate
            </div>
            {candidateFeed.length === 0 ? (
              <div className="card" style={{ marginBottom: 32, padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                No notes from the candidate yet.
              </div>
            ) : (
              <div style={{ marginBottom: 32 }}>
                {candidateFeed.map(entry => (
                  <div
                    key={entry.id}
                    style={{
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: 16,
                      marginBottom: 10,
                    }}
                  >
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                      {entry.timestampMs
                        ? new Date(entry.timestampMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—'}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {entry.text}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Remove candidate */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 24 }}>
              {removeError && <div className="error-message" style={{ marginBottom: 12 }}>{removeError}</div>}
              <button
                className="btn-danger"
                onClick={() => setConfirmRemove(true)}
                disabled={removing}
              >
                {removing ? 'Removing…' : 'Remove Candidate'}
              </button>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                Unlinks this candidate from your caseload. Their seat and enrollment record are preserved.
              </div>
            </div>
          </>
        ) : null}
      </div>
      {confirmRemove && (
        <ConfirmModal
          title="Remove Candidate"
          message={`Remove ${profile?.name || 'this candidate'} from your caseload? This cannot be undone.`}
          confirmLabel="Remove Candidate"
          onConfirm={() => { setConfirmRemove(false); handleRemove(); }}
          onCancel={() => setConfirmRemove(false)}
        />
      )}
      {selectedJobId && (
        <JobDetailModal jobId={selectedJobId} onClose={() => setSelectedJobId(null)} />
      )}
    </Layout>
  );
}

function FactRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 110, flexShrink: 0, paddingTop: 1 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.4 }}>{value}</div>
    </div>
  );
}

function SearchQualityBody({ snap }: { snap: any }) {
  const om = snap.dimensions?.opportunityMomentum;
  const sp = snap.dimensions?.searchPrecision;
  const cohort = snap.cohort;
  const isOM = snap.headlineDimension === 'opportunityMomentum';
  const trendArrow = snap.headlineDirection === 'improving' ? '↑'
    : snap.headlineDirection === 'declining' ? '↓' : '→';
  const omPct = cohort?.percentiles?.conversionRate != null ? Math.round(cohort.percentiles.conversionRate * 100) : null;
  const spPct = cohort?.percentiles?.avgFitScore != null ? Math.round(cohort.percentiles.avgFitScore * 100) : null;

  let cohortLine: string;
  if (!cohort || cohort.tier === 'self') {
    cohortLine = 'Not enough peers yet for cohort comparison.';
  } else {
    const qualifier = cohort.tier === 'hedged' ? 'Directional comparison' : 'Comparison';
    const pcts: string[] = [];
    if (omPct !== null) pcts.push(`${ordinal(omPct)} percentile on conversion`);
    if (spPct !== null) pcts.push(`${ordinal(spPct)} percentile on fit`);
    cohortLine = `${qualifier} across ${cohort.cohortSize} candidates${pcts.length ? ' · ' + pcts.join(', ') : ''}`;
  }

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 32px', marginBottom: 14 }}>
        <MarketStat
          value={om?.conversionRate != null ? `${Math.round(om.conversionRate * 100)}%` : '—'}
          label={`Conversion Rate${isOM ? ' ' + trendArrow : ''}`}
          sub={`${om?.totalApplications ?? 0} applied · ${om?.confidence != null ? Math.round(om.confidence * 100) + '%' : '—'} confidence`}
        />
        <MarketStat
          value={sp?.avgFitScore != null ? `${Math.round(sp.avgFitScore)}%` : '—'}
          label={`Avg Fit Score${!isOM ? ' ' + trendArrow : ''}`}
          sub={sp?.resumeTailoringRate != null ? `${Math.round(sp.resumeTailoringRate * 100)}% tailoring rate` : 'tailoring n/a'}
        />
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: snap.narrativeText ? 12 : 0 }}>
        {cohortLine}
      </div>
      {snap.narrativeText && (
        <div style={{
          borderLeft: '3px solid var(--border)',
          paddingLeft: 12,
          fontSize: 13,
          color: 'var(--text-secondary)',
          lineHeight: 1.6,
          fontStyle: 'italic',
        }}>
          {snap.narrativeText}
        </div>
      )}
    </>
  );
}

function MarketStat({ value, label, sub }: { value: string; label: string; sub: string }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 22, fontWeight: 700 }}>{value}</span>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function CategoryTag({ category }: { category: string }) {
  const label = CATEGORY_LABELS[category as NoteCategory] ?? category;
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      padding: '2px 7px',
      borderRadius: 3,
      background: 'var(--bg-secondary)',
      color: 'var(--text-muted)',
      border: '1px solid var(--border)',
    }}>
      {label}
    </span>
  );
}

function EntryLabel({ type, visibility }: { type: 'ai' | 'coach'; visibility?: 'private' | 'shared' }) {
  const isAI = type === 'ai';
  const isShared = visibility === 'shared';
  const label = isAI
    ? 'AI Direction'
    : isShared ? 'Coach Note · Shared' : 'Coach Note · Private';
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      padding: '3px 8px',
      borderRadius: 3,
      background: isAI ? 'var(--bg-secondary)' : 'var(--bg-card)',
      color: isAI ? 'var(--text-secondary)' : 'var(--text-primary)',
      border: `1px solid ${isAI ? 'transparent' : 'var(--border)'}`,
    }}>
      {!isAI && (isShared ? '👁 ' : '🔒 ')}{label}
    </span>
  );
}

function StatusBadge({ status }: { status: 'pending' | 'approved' | null }) {
  if (status === 'approved') return <span className="badge badge-active">Approved</span>;
  if (status === 'pending') {
    return (
      <span className="badge" style={{ background: 'var(--warning-dim)', color: 'var(--warning)' }}>
        Pending
      </span>
    );
  }
  return <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>;
}

function JobDetailModal({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const [job, setJob] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError('');
    setJob(null);
    getDoc(doc(db, 'jobs', jobId)).then(snap => {
      if (cancelled) return;
      if (!snap.exists()) { setFetchError('Job not found.'); setLoading(false); return; }
      const d = snap.data();
      setJob({
        title: d.role ?? d.title ?? '',
        company: d.company ?? '',
        date: fmtDate(d.createdAt),
        location: d.location ?? null,
        salary: d.salary ?? null,
        input: d.input ?? null,
        fit: d.fit ?? null,
        components: d.components ?? null,
        skillsPresent: Array.isArray(d.skillsPresent) && d.skillsPresent.length > 0 ? d.skillsPresent : null,
        skillsMissing: Array.isArray(d.skillsMissing) && d.skillsMissing.length > 0 ? normalizeSkillGaps(d.skillsMissing) : null,
        statusHistory: d.statusHistory ?? [],
      });
      setLoading(false);
    }).catch((e: any) => {
      if (!cancelled) { setFetchError(e.message || 'Failed to load job.'); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [jobId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const sortedHistory = job
    ? [...job.statusHistory].sort((a, b) => historyMs(a.changedAt) - historyMs(b.changedAt))
    : [];

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, width: '90%', maxWidth: 680, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        {/* Sticky header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            {job ? (
              <>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{job.title || '—'}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>
                  {job.company}{job.date !== '—' && ` · ${job.date}`}
                </div>
                {(job.location || job.salary) && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {[job.location, job.salary].filter(Boolean).join(' · ')}
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-muted)' }}>—</div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ marginLeft: 16, background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-secondary)', lineHeight: 1, padding: '0 4px', flexShrink: 0 }}
            aria-label="Close"
          >×</button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', padding: '20px 20px 24px', flex: 1 }}>
          {loading && <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>}
          {fetchError && <div className="error-message">{fetchError}</div>}
          {job && (
            <div style={{ display: 'grid', gap: 24 }}>

              {/* Job Description */}
              {job.input && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 8 }}>Job Description</div>
                  {job.input.startsWith('http') && job.input.length < 200 ? (
                    <a href={job.input} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--accent-text)', wordBreak: 'break-all' }}>{job.input}</a>
                  ) : (
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{job.input}</div>
                  )}
                </div>
              )}

              {/* Fit Score + Breakdown */}
              {job.fit != null && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 10 }}>
                    Fit Score{' '}
                    <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'var(--bg-secondary)', color: 'var(--text-muted)', border: '1px solid var(--border)', verticalAlign: 'middle' }}>AI</span>
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 14 }}>{job.fit}</div>
                  {job.components && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      {SCORE_COMPONENTS.map(({ key, label, sub }) => {
                        const val = job.components![key];
                        if (val == null) return null;
                        return (
                          <div key={key} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                            <div style={{ minWidth: 32, flexShrink: 0 }}>
                              <span style={{ fontSize: 14, fontWeight: 600 }}>{val}</span>
                            </div>
                            <div>
                              <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{label}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{sub}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Skills */}
              {(job.skillsPresent || job.skillsMissing) && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 10 }}>Skills</div>
                  <div style={{ display: 'grid', gap: 14 }}>
                    {job.skillsPresent && (
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>You Have</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {job.skillsPresent.map((s, i) => (
                            <span key={i} style={{ fontSize: 12, padding: '2px 8px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)' }}>{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {job.skillsMissing && (
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>Skill Gaps</div>
                        <div style={{ display: 'grid', gap: 12 }}>
                          {SKILL_TIERS.map(({ key, label: tierLabel }) => {
                            const items = job.skillsMissing!.filter(s => s.importance === key);
                            if (items.length === 0) return null;
                            return (
                              <div key={key}>
                                <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 6 }}>{tierLabel}</div>
                                <div style={{ display: 'grid', gap: 7 }}>
                                  {items.map((s, i) => (
                                    <div key={i}>
                                      <span style={{ fontSize: 13, fontWeight: 500 }}>{s.skill}</span>
                                      {s.reason && <span style={{ fontSize: 13, color: 'var(--text-secondary)', marginLeft: 8 }}>— {s.reason}</span>}
                                      {s.bridge && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.bridge}</div>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Status History */}
              {sortedHistory.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 8 }}>Status History</div>
                  <div style={{ display: 'grid', gap: 5 }}>
                    {sortedHistory.map((e, i) => {
                      const ms = historyMs(e.changedAt);
                      return (
                        <div key={i} style={{ display: 'flex', gap: 12, fontSize: 13 }}>
                          <span style={{ color: 'var(--text-muted)', minWidth: 100, flexShrink: 0 }}>
                            {ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </span>
                          <span>{fmtStatus(e.status)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
